import { useQuery } from '@tanstack/react-query';
import {
    GridCallbackDetails,
    GridColDef,
    GridPaginationModel,
    GridRowParams,
    GridSortModel,
} from '@mui/x-data-grid';

import { DataGrid, DataGridCard } from '@barboxx/admin-components';
import {
    ACTIVE_WIDTH,
    ALCOHOL_TYPE_WIDTH,
    NAME_MIN_WIDTH,
    PRICE_TIER_WIDTH,
    useBusinessSelector,
} from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import {
    BusinessPriceTier,
    PourType,
    PriceTier,
    ProductSubtype,
    ProductType,
} from '@barboxx/models';

type Columns = GridColDef<BusinessPriceTier.DetailsResponse>[];
const COLUMNS: Columns = [
    {
        field: 'displayOrder',
        headerName: 'Display Order',
        width: 120,
        editable: true,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
    },
    {
        field: 'defaultUpCharge',
        headerName: 'Default Up Charge',
        width: 150,
        editable: true,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
    },
    {
        field: 'isDefault',
        headerName: 'Default',
        width: ACTIVE_WIDTH,
        editable: true,
        type: 'boolean',
        align: 'center',
        headerAlign: 'center',
    },
];

interface Props {
    data: BusinessPriceTier[];
    size: number;
    page: number;
    total: number;
    onRowClick: (id: string) => void;
    onChangeSort: (sort: GridSortModel) => void;
    handleChangePageModel: (
        model: GridPaginationModel,
        details: GridCallbackDetails
    ) => void;
}

export function BusinessPriceTierTable(props: Props) {
    const {
        data = [],
        total,
        size,
        page,
        onRowClick,
        handleChangePageModel,
        onChangeSort,
    } = props;

    const { businessList } = useBusinessSelector();

    const priceTiersQuery = useQuery({
        queryKey: ['priceTiers'],
        queryFn: () => Api.Core.PriceTier.getPriceTiers({ size: 50 }),
    });

    const pourTypesQuery = useQuery({
        queryKey: ['pourTypes'],
        queryFn: () => Api.Servings.PourType.getPourTypes({ size: 50 }),
    });

    const productTypesQuery = useQuery({
        queryKey: ['productTypes'],
        queryFn: () => Api.Products.Types.getProductTypes({ size: 50 }),
    });

    const productSubtypesQuery = useQuery({
        queryKey: ['productSubtypes'],
        queryFn: () => Api.Products.Subtypes.getProductSubtypes({ size: 1000 }),
    });

    const handleRowClick = (param: GridRowParams) => {
        onRowClick(param.id as string);
    };

    const columns: Columns = [
        {
            field: 'businessId',
            headerName: 'Business',
            width: NAME_MIN_WIDTH,
            type: 'singleSelect',
            valueOptions(params) {
                return (
                    businessList.map(business => ({
                        value: business.id,
                        label: business.place.name,
                    })) ?? []
                );
            },
        },
        {
            field: 'priceTierId',
            headerName: 'Price Tier',
            editable: true,
            width: PRICE_TIER_WIDTH,
            type: 'singleSelect',
            flex: 1,
            valueOptions(params) {
                return (
                    priceTiersQuery.data?.data?.results.map(
                        (priceTier: PriceTier) => ({
                            value: priceTier.id,
                            label: priceTier.name,
                        })
                    ) ?? []
                );
            },
        },
        {
            field: 'pourTypeId',
            headerName: 'Pour Type',
            editable: true,
            width: PRICE_TIER_WIDTH,
            type: 'singleSelect',
            flex: 1,
            valueOptions(params) {
                return (
                    pourTypesQuery.data?.data?.results.map(
                        (pourType: PourType) => ({
                            value: pourType.id,
                            label: pourType.name,
                        })
                    ) ?? []
                );
            },
        },
        {
            field: 'productTypeId',
            headerName: 'Alcohol Type',
            editable: true,
            width: ALCOHOL_TYPE_WIDTH,
            type: 'singleSelect',
            flex: 1,
            valueOptions(params) {
                return (
                    productTypesQuery.data?.data?.results.map(
                        (productType: ProductType) => ({
                            value: productType.id,
                            label: productType.name,
                        })
                    ) ?? []
                );
            },
        },
        {
            field: 'productSubtypeId',
            headerName: 'Alcohol Subtype',
            editable: true,
            width: ALCOHOL_TYPE_WIDTH,
            type: 'singleSelect',
            flex: 1,
            valueOptions(params) {
                return (
                    productSubtypesQuery.data?.data?.results.map(
                        (productSubtype: ProductSubtype) => ({
                            value: productSubtype.id,
                            label: productSubtype.name,
                        })
                    ) ?? []
                );
            },
        },
        ...COLUMNS,
    ];

    return (
        <DataGridCard title="Business Price Tiers">
            <DataGrid
                rows={data}
                columns={columns}
                paginationModel={{
                    page,
                    pageSize: size,
                }}
                onPaginationModelChange={handleChangePageModel}
                rowCount={total}
                disableColumnMenu
                onRowClick={handleRowClick}
                onSortModelChange={onChangeSort}
            />
        </DataGridCard>
    );
}
